import { badgeMarkup, routineBounds, routineDates } from '../../ui/routines.js';
/**
 * Gantt View Module
 * Pure view: receives state, emits events via callbacks
 * Timeline chart with draggable/resizable bars
 */

import * as Store from "../../store/index.js";
import { getDocState, saveDocState } from "../../../../shared/local-doc-state.js";
import * as MultiSelect from "../../ui/multi-select.js";
import { renderHorizonControl } from "../../ui/horizon-control.js";
import { calendarDay, dateAtDay, dateStringAtDay } from "../../../../engine/core/calendar-day.js";
import { getGanttTaskRange } from '../../../../engine/core/gantt-task-range.js';
import { buildGanttCalendar } from "../../../../engine/core/gantt-calendar.js";
import { safeColor, escapeAttr, escapeHtml as escapeHtmlCore } from "../../../../engine/core/validation.js";
import { setupNotePreviewOnBadge } from "../../ui/note-preview.js";
import { getLinkedBadge } from "../linked-badge.js";
import { centeringOffset } from '../../ui/view-centering.js';
import { t } from "../../../../shared/i18n/index.js";
import { viewLabel } from "../../../../engine/core/view-names.js";

// Note: beginBatch/endBatch are passed via callbacks to avoid direct sync imports

// Module state
let container = null;
let callbacks = null;
let currentState = null;

// DOM references
let daysEl = null;
let scaleEl = null;
let labelsEl = null;
let bodyEl = null;
let headerEl = null;

// Zoom state
let ganttZoom = 1;

// Row height state (as percentage of default, 100% = 32px)
const DEFAULT_ROW_HEIGHT_PX = 32;
// Height of one calendar header line (year, quarter, month, week, day);
// keep in sync with .gantt-timeline-row in main.css.
const HEADER_ROW_PX = 18;
const MIN_ROW_HEIGHT_PCT = 10;
const MAX_ROW_HEIGHT_PCT = 300;
// Task list pane width: draggable, saved per map like the sidebar width.
const MIN_LABELS_WIDTH = 140;
const MAX_LABELS_WIDTH_RATIO = 0.7;
let ganttRowHeightPct = 100; // percentage

// Cached timeline data
let timelineCache = {
  minD: null,
  dayW: 40,
  timelineWidth: 0,
};

// Batched rendering
let renderScheduled = false;
let warnedMissingCallbacks = false; // One-time warning for missing callbacks
let timelineBounds = null;
let scrollAnchor = null;
let renderFrame = null;
let viewportObserver = null;
let calendarPaintBounds = null;
let midnightTimer = null;

// AbortController for drag-drop event delegation (prevents stacking listeners on re-render)
let dragDropAbort = null;

// The rows of the last render, in order, and those drawn: the ones on screen
// and WINDOW_MARGIN above and below (all of them up to about 450 rows).
// Spacers stand for the others: rows have a fixed height. Drawing every row
// took seconds per change on large maps (20,000 items: 2.5 s per edit;
// docs/internal/perf/2026-10-08-big-maps.md).
let ganttRows = [];
let rowContext = null; // what drawing a row needs, from the last render
let drawnRows = null; // { from, to, rowHeight }, `to` excluded
let allRowsDrawn = false; // withAllRowsDrawn (the SVG export)
const WINDOW_MARGIN = 200;
// Rows left beyond the screen at which the next ones are drawn.
const WINDOW_EDGE = 50;

/**
 * Warn once if CRDT batch callbacks are missing (helps detect wiring issues in production)
 */
function warnMissingBatchCallbacks() {
  if (!warnedMissingCallbacks && (!callbacks?.beginBatch || !callbacks?.endBatch)) {
    console.warn("[Gantt] CRDT batch callbacks (beginBatch/endBatch) not provided - batching disabled. Check view wiring in main.js sharedCallbacks.");
    warnedMissingCallbacks = true;
  }
}

/**
 * Render the Gantt view
 * @param {HTMLElement} containerEl - Container element
 * @param {Object} state - { data, focusId, selectedId, hideCompleted, ganttZoom }
 * @param {Object} cbs - Callbacks for actions
 */
export function render(containerEl, state, cbs) {
  // Full view remounts must release the previous observers and key handlers.
  if (container) destroy();
  container = containerEl;
  callbacks = cbs;
  currentState = state;
  ganttZoom = state.ganttZoom || 1;
  ganttRowHeightPct = state.ganttRowHeight || 100;

  // Create the Gantt structure (matching original layout)
  container.innerHTML = `
    <div class="gantt-scale-container" id="g-scale">
      <div class="breadcrumb" id="g-crumbs"></div>
    </div>
    <div class="gantt-container">
      <div class="gantt-labels" id="g-labels-pane">
        <div class="gantt-labels-head">${t("views.gantt.tasks")}</div>
        <div class="gantt-labels-body" id="g-labels"></div>
        <div class="gantt-labels-resizer" id="g-labels-resizer" title="${t("views.gantt.resizeList")}"></div>
      </div>
      <div class="gantt-timeline-wrapper">
        <div class="gantt-timeline-header" id="g-header">
          <div class="gantt-days" id="g-days"></div>
        </div>
        <div class="gantt-timeline-body" id="g-body"></div>
      </div>
    </div>
    <div class="controls">
      <button class="ctrl-btn" id="g-zin">+</button>
      <div class="zoom-lvl" id="g-zlvl">${Math.round(ganttZoom * 100)}%</div>
      <button class="ctrl-btn" id="g-zout">−</button>
      <div class="row-height-controls" style="margin-top: 10px; border-top: 1px solid var(--border-color); padding-top: 8px;">
        <button class="ctrl-btn" id="g-rhin" title="${t("views.gantt.rowHeightUp")}">↕+</button>
        <div class="zoom-lvl" id="g-rhlvl" title="${t("views.gantt.rowHeight")}">${ganttRowHeightPct}%</div>
        <button class="ctrl-btn" id="g-rhout" title="${t("views.gantt.rowHeightDown")}">↕−</button>
      </div>
    </div>
  `;

  daysEl = container.querySelector("#g-days");
  scaleEl = container.querySelector("#g-scale");
  labelsEl = container.querySelector("#g-labels");
  bodyEl = container.querySelector("#g-body");
  headerEl = container.querySelector("#g-header");

  setupZoomHandler();
  setupZoomButtons();
  setupRowHeightButtons();
  setupLabelsResize();
  viewportObserver = new ResizeObserver(() => renderGantt());
  viewportObserver.observe(bodyEl);
  scheduleMidnightRefresh();
  renderGantt();
}

/**
 * Setup zoom button click handlers
 */
function setupZoomButtons() {
  const zoomInBtn = container.querySelector("#g-zin");
  const zoomOutBtn = container.querySelector("#g-zout");

  if (zoomInBtn) {
    zoomInBtn.addEventListener("click", () => {
      ganttZoom = Math.min(10, ganttZoom * 1.2);
      if (callbacks.onZoomChange) {
        callbacks.onZoomChange(ganttZoom);
      }
      renderGantt();
    });
  }

  if (zoomOutBtn) {
    zoomOutBtn.addEventListener("click", () => {
      ganttZoom = Math.max(0.01, ganttZoom / 1.2);
      if (callbacks.onZoomChange) {
        callbacks.onZoomChange(ganttZoom);
      }
      renderGantt();
    });
  }
}

/**
 * Get row height in pixels from percentage
 */
function getRowHeightPx() {
  return Math.round((ganttRowHeightPct / 100) * DEFAULT_ROW_HEIGHT_PX);
}

/**
 * Make the task list pane resizable by dragging its right edge; the width is
 * saved per map (local, like the sidebar width) and restored on render.
 */
function setupLabelsResize() {
  const pane = container.querySelector("#g-labels-pane");
  const handle = container.querySelector("#g-labels-resizer");
  if (!pane || !handle) return;
  const docId = Store.get("currentDocId");
  const clamp = (w) => Math.round(Math.max(MIN_LABELS_WIDTH,
    Math.min(w, (container.clientWidth || window.innerWidth) * MAX_LABELS_WIDTH_RATIO)));
  const apply = (w) => {
    pane.style.width = `${w}px`;
    pane.style.minWidth = `${w}px`;
  };
  const saved = Number(getDocState(docId)?.ganttLabelsWidth);
  if (saved > 0) apply(clamp(saved));

  handle.addEventListener("mousedown", (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = pane.offsetWidth;
    handle.classList.add("dragging");
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev) => apply(clamp(startWidth + ev.clientX - startX));
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      handle.classList.remove("dragging");
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      if (docId) saveDocState(docId, { ganttLabelsWidth: pane.offsetWidth });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  });
}

/**
 * Setup row height button click handlers
 */
function setupRowHeightButtons() {
  const rowHeightInBtn = container.querySelector("#g-rhin");
  const rowHeightOutBtn = container.querySelector("#g-rhout");

  if (rowHeightInBtn) {
    rowHeightInBtn.addEventListener("click", () => {
      ganttRowHeightPct = Math.min(MAX_ROW_HEIGHT_PCT, ganttRowHeightPct + 10);
      if (callbacks.onRowHeightChange) {
        callbacks.onRowHeightChange(ganttRowHeightPct);
      }
      renderGantt();
    });
  }

  if (rowHeightOutBtn) {
    rowHeightOutBtn.addEventListener("click", () => {
      ganttRowHeightPct = Math.max(MIN_ROW_HEIGHT_PCT, ganttRowHeightPct - 10);
      if (callbacks.onRowHeightChange) {
        callbacks.onRowHeightChange(ganttRowHeightPct);
      }
      renderGantt();
    });
  }
}

/**
 * Update zoom level display
 */
function updateZoomDisplay() {
  const zoomLvl = container.querySelector("#g-zlvl");
  if (zoomLvl) {
    zoomLvl.textContent = Math.round(ganttZoom * 100) + "%";
  }
}

/**
 * Update row height display
 */
function updateRowHeightDisplay() {
  const rowHeightLvl = container.querySelector("#g-rhlvl");
  if (rowHeightLvl) {
    rowHeightLvl.textContent = ganttRowHeightPct + "%";
  }
}

/**
 * Schedule a render for the next animation frame (batches multiple requests)
 */
function scheduleRender() {
  if (renderScheduled) return;
  renderScheduled = true;

  renderFrame = requestAnimationFrame(() => {
    renderScheduled = false;
    renderFrame = null;
    if (container && bodyEl && callbacks) doRenderGantt();
  });
}

/**
 * Render the Gantt chart - public API
 */
function renderGantt() {
  scheduleRender();
}

/**
 * Actually render the Gantt chart
 */
function doRenderGantt() {
  const savedTop = bodyEl.scrollTop;
  // When row height changes, keep the same task and position within that task
  // at the viewport center. Reusing the old pixel offset would jump between rows.
  let rowAnchor = null;
  const previousRowHeight = drawnRows?.rowHeight;
  if (previousRowHeight && previousRowHeight !== getRowHeightPx()) {
    const centerRow = (savedTop + bodyEl.clientHeight / 2) / previousRowHeight;
    const index = Math.floor(centerRow);
    const previousRow = ganttRows[index];
    if (previousRow) {
      rowAnchor = { nodeId: previousRow.node.id, fraction: centerRow - index };
    }
  }
  const anchor = scrollAnchor || (timelineCache.minD ? {
    day: calendarDay(timelineCache.minD) + bodyEl.scrollLeft / timelineCache.dayW,
    x: 0,
  } : null);
  scrollAnchor = null;
  renderBreadcrumbs();

  const escapeHtml = callbacks.escapeHtml || ((s) => s);

  // Build tag-visibility set + highlight ancestor set up-front. Same rules as
  // mindmap/bubble: Done-cascade prunes the subtree; otherwise a self-hidden
  // node is dropped from the row list but the walk continues into its
  // children so non-hidden descendants still appear.
  const hiddenTagIds = callbacks?.getHiddenTagIds?.() || [];
  const highlightedTagIds = callbacks?.getHighlightedTagIds?.() || [];
  const doneTagId = callbacks?.getDoneTagId?.() || null;
  const nodeIsDoneFn = callbacks?.nodeIsDone || null;
  const tagFilter = callbacks?.makeTagFilter?.();
  const hide = !!currentState.hideCompleted;
  const doneHidden = hide || (!!doneTagId && hiddenTagIds.includes(doneTagId));
  const isDone = (n) => n.status === "done" || !!nodeIsDoneFn?.(n);
  const outOfHorizon = callbacks?.getHorizonPredicate?.() || null;
  const isTagHidden = (n) => {
    if (outOfHorizon && outOfHorizon(n)) return true;
    if (tagFilter?.isHidden(n, hiddenTagIds)) return true;
    return false;
  };
  const visibleIds = new Set();
  if (currentState.data) {
    const walk = (n) => {
      if (doneHidden && isDone(n)) return;
      if (!isTagHidden(n)) visibleIds.add(n.id);
      (n.children || []).forEach(walk);
    };
    walk(currentState.data);
  }

  // Highlight: tag-match-ancestor doesn't apply in gantt's flat list — a row
  // is either a self-match or it isn't.
  const isTagMatch = (n) => !!tagFilter?.isMatch(n, highlightedTagIds);
  const tagFiltering = highlightedTagIds.length > 0;
  labelsEl.classList.toggle("tag-filtering", tagFiltering);
  bodyEl.classList.toggle("tag-filtering", tagFiltering);

  // Include display-only placeholders in the visible calendar range.
  // rootId is dynamic (not always "root"), e.g., shared docs
  const rootId = currentState.data?.id;
  const allNodes = callbacks.getAll
    ? callbacks.getAll().filter((n) => n.id !== rootId)
    : [];
  const taskRanges = allNodes.filter(n => visibleIds.has(n.id)).map(n => getGanttTaskRange(n)).filter(Boolean);

  // Execution layer (planned vs actual): nodes that have been on a Today list
  // get an actual span — first entered → last worked (or today while still
  // listed) — even without planned dates. They count for the timeline range.
  execStats = callbacks?.getExecutionStats?.() || null;
  const execSpans = [];
  if (execStats) {
    for (const n of allNodes) {
      if (!visibleIds.has(n.id)) continue;
      const span = executionSpanOf(n.id);
      if (span) execSpans.push(span);
      const routineSpan=routineBounds(n.id);if(routineSpan)execSpans.push(routineSpan);
      const returnDate = callbacks.getTaskPostponement?.(n.id)?.until;
      if (returnDate) execSpans.push({ start: dateAtDay(calendarDay(returnDate)), end: dateAtDay(calendarDay(returnDate)) });
    }
  }

  // The calendar has its own range, independent of the end of the tasks.
  // Keep spare viewports at either end, extending them as the user scrolls.
  const today = localDay(new Date());
  const taskDays = taskRanges.flatMap(range => [range.startDay, range.endDay]);
  for (const span of execSpans) taskDays.push(calendarDay(span.start), calendarDay(span.end));
  const finiteDays = taskDays.filter(Number.isFinite);
  const taskMin = finiteDays.length ? Math.min(...finiteDays) : calendarDay(today);
  const taskMax = finiteDays.length ? Math.max(...finiteDays) : calendarDay(today);
  const dayW = 40 * ganttZoom;
  const visibleDays = Math.max(1, Math.ceil(bodyEl.clientWidth / dayW));
  const padding = Math.max(7, visibleDays * 2);
  timelineBounds = {
    start: Math.floor(Math.min(timelineBounds?.start ?? taskMin, taskMin - padding, (anchor?.day ?? taskMin) - padding)),
    end: Math.ceil(Math.max(timelineBounds?.end ?? taskMax, taskMax + padding, (anchor?.day ?? taskMin) + padding)),
  };
  const minD = dateAtDay(timelineBounds.start);
  const totalDays = timelineBounds.end - timelineBounds.start + 1;

  // Task geometry always uses individual calendar days. Calendar rendering is
  // bounded to the viewport, so exploring a long range never coarsens dates.
  timelineCache = { minD, dayW, timelineWidth: totalDays * dayW };
  const target = anchor || { day: taskMin, x: 32 };
  const targetLeft = Math.max(0, Math.min(
    Math.max(0, timelineCache.timelineWidth - bodyEl.clientWidth),
    (target.day - timelineBounds.start) * dayW - target.x,
  ));

  // Add controls to scale container (breadcrumbs are already there)
  // Remove existing controls first to avoid duplicates
  const existingControls = scaleEl.querySelector(".gantt-controls");
  if (existingControls) {
    existingControls.remove();
  }

  const controlsHtml = `<div class="gantt-controls">
    <button class="gantt-add-btn" id="gantt-add-btn">${t("views.gantt.addTask")}</button>
    <button class="gantt-today-btn" id="gantt-today-btn">${t("common.today")}</button>
    <div class="gantt-zoom-hint">${t("views.gantt.zoomHint")}</div>
  </div>`;

  scaleEl.insertAdjacentHTML("beforeend", controlsHtml);

  // Set the header height before laying out task rows and restoring scroll.
  const calendar = calendarMarkup(targetLeft);
  daysEl.innerHTML = calendar.header;
  daysEl.style.width = `${timelineCache.timelineWidth}px`;
  // Same height as the timeline header, border included, so rows line up.
  const labelsHead = container.querySelector('.gantt-labels-head');
  if (labelsHead) labelsHead.style.height = `${headerEl.offsetHeight || calendar.height}px`;

  // Build rows
  const focused = callbacks.find(currentState.focusId);
  const rows = collectRows(focused, { doneHidden, isDone, isTagHidden });
  const isViewer = callbacks.isReadOnly && callbacks.isReadOnly();

  ganttRows = rows;
  rowContext = { isViewer, isTagMatch, escapeHtml, dayW };

  // Setup label drag-drop for tree restructuring
  setupLabelDragDrop();

  bodyEl.innerHTML =
    `<div class="gantt-timeline-scroll" data-start-date="${dateStringAtDay(timelineBounds.start)}" data-end-date="${dateStringAtDay(timelineBounds.end)}" style="width:${timelineCache.timelineWidth}px">` +
    `<div class="gantt-calendar-layer" aria-hidden="true">${calendar.grid}</div>` +
    "</div>";

  // The rows around where the list stays (the same task at the center after
  // a row height change, else the same scroll), then the scroll itself.
  const anchorIndex = rowAnchor ? rows.findIndex((row) => row.node.id === rowAnchor.nodeId) : -1;
  const targetTop = anchorIndex >= 0
    ? (anchorIndex + rowAnchor.fraction) * getRowHeightPx() - bodyEl.clientHeight / 2
    : savedTop;
  drawRows(targetTop, true);

  // The timeline's horizontal scrollbar lets it scroll that much further down
  // than the task list: pad the list by the same amount so the rows stay in
  // line at the bottom.
  labelsEl.style.paddingBottom = `${Math.max(0, bodyEl.offsetHeight - bodyEl.clientHeight)}px`;

  // Restore the same calendar position after rebuilding/expanding the grid.
  bodyEl.scrollLeft = targetLeft;
  bodyEl.scrollTop = targetTop;
  headerEl.scrollLeft = bodyEl.scrollLeft;
  labelsEl.scrollTop = bodyEl.scrollTop;
  drawRows(); // the scroll may have been bounded: draws only if needed
  setupScrollSync();

  // Setup add button
  const addBtn = container.querySelector("#gantt-add-btn");
  if (addBtn) {
    addBtn.addEventListener("click", () => {
      if (isViewer) return;
      if (callbacks.onNodeAdd) {
        callbacks.onNodeAdd(currentState.focusId);
      }
    });
  }
  container.querySelector('#gantt-today-btn')?.addEventListener('click', () => {
    scrollAnchor = { day: calendarDay(new Date()), x: bodyEl.clientWidth / 3 };
    renderGantt();
  });

  // Update zoom level display
  updateZoomDisplay();
  updateRowHeightDisplay();
}

/** One row of the task list. */
function labelRowHtml(row) {
  const { isViewer, isTagMatch, escapeHtml } = rowContext;
  const indent = row.level * 16;
  const isDone = callbacks?.nodeIsDone?.(row.node) || row.node.status === "done";
  const name = row.node.name || row.node.text || t("common.untitled");
  const hasChildren = row.node.children && row.node.children.length > 0;
  const isCollapsed = row.node.collapsed;

  // Collapse toggle - only for nodes with children, hidden for viewers.
  // Leaves get an empty spacer of the same width, so names line up by
  // level (a toggle used to push parents further right than children).
  const collapseToggle = isViewer ? ""
    : hasChildren
      ? `<span class="gantt-collapse-toggle" data-id="${escapeAttr(row.node.id)}" title="${isCollapsed ? t("views.expand") : t("views.collapse")}">${isCollapsed ? "+" : "−"}</span>`
      : '<span class="gantt-collapse-toggle spacer" aria-hidden="true">−</span>';

  // Reference node badge — distinct icon + state via shared helper.
  const _linked = getLinkedBadge(row.node);
  const refBadge = _linked.isLinkedStub
    ? `<span class="gantt-ref-badge ${escapeAttr(_linked.className)}" data-id="${escapeAttr(row.node.id)}" title="${escapeHtml(_linked.title)}">${_linked.icon}</span>`
    : "";

  // Notes badge - only for nodes with notes (skip focused root)
  const rootId = currentState.data?.id;
  const isFocusedRoot =
    rootId &&
    currentState.focusId === rootId &&
    row.node.id === rootId;
  const notesBadge =
    !isFocusedRoot && row.node.notes && row.node.notes.trim()
      ? `<span class="gantt-notes-badge" data-id="${escapeAttr(row.node.id)}">🗒️</span>`
      : "";

  // Build class list
  const classes = ["gantt-label-row"];
  if (row.isGroup) classes.push("group");
  if (isDone) classes.push("done");
  if (isCollapsed) classes.push("collapsed");
  if (isTagMatch(row.node)) classes.push("tag-match");
  return `<div class="${classes.join(" ")}"
             style="padding-left:${20 + indent}px;height:${getRowHeightPx()}px"
             data-id="${escapeAttr(row.node.id)}"
             data-level="${row.level}"
             data-status="${callbacks?.getEffectiveStatus?.(row.node) || row.node.status || 'todo'}"
             ${isViewer ? "" : 'draggable="true"'}>
  ${isViewer ? "" : '<span class="gantt-drag-handle">⋮⋮</span>'}
  ${collapseToggle}
  <div class="gantt-label-dot" style="background:${safeColor(row.node.color)}"></div>
  ${refBadge}
  ${notesBadge}
  <span class="gantt-label-name">${escapeHtml(name)}</span>${badgeMarkup(row.node.id)}
    </div>`;
}

/** Handlers of the drawn task-list rows (fold toggle, note badge). */
function attachLabelHandlers() {
  // Setup collapse toggle click handlers (hidden for read-only viewers via rendering)
  labelsEl.querySelectorAll(".gantt-collapse-toggle:not(.spacer)").forEach((toggle) => {
    toggle.addEventListener("click", (e) => {
      e.stopPropagation();
      if (rowContext?.isViewer) return;
      const nodeId = toggle.dataset.id;
      const node = callbacks.find(nodeId);
      if (node) {
        node.collapsed = !node.collapsed;
        if (callbacks.onUpdateNode) {
          callbacks.onUpdateNode(nodeId, { collapsed: node.collapsed });
        }
      }
    });
  });

  // Setup notes badge click + preview handlers
  labelsEl.querySelectorAll(".gantt-notes-badge").forEach((badge) => {
    const nodeId = badge.dataset.id;
    const node = callbacks.find?.(nodeId);
    if (node?.notes) {
      setupNotePreviewOnBadge(badge, node.notes, callbacks.getDocId?.(), callbacks.getDek?.());
    }
    badge.addEventListener("click", (e) => {
      e.stopPropagation();
      if (callbacks.onOpenModal) {
        callbacks.onOpenModal(nodeId);
      }
    });
  });
}

/** True while a bar or a task-list row is being dragged: its element must stay. */
function rowDragging() {
  return !!(bodyEl.querySelector(".gantt-bar.dragging") || labelsEl.querySelector(".gantt-label-row.dragging"));
}

/**
 * Draw the rows around `top` (default: the scroll), task list and timeline,
 * with spacers for the others, and their handlers. Without `force`, only
 * when the screen nears an end of the drawn rows, and not during a drag.
 */
function drawRows(top = bodyEl?.scrollTop ?? 0, force = false) {
  if (!bodyEl || !labelsEl || !rowContext) return;
  const rowHeight = getRowHeightPx();
  const total = ganttRows.length;
  const first = Math.min(total, Math.max(0, Math.floor(top / rowHeight)));
  const last = Math.min(total, Math.max(first, Math.ceil((top + bodyEl.clientHeight) / rowHeight)));
  if (!force && drawnRows) {
    const covered = first >= drawnRows.from + (drawnRows.from > 0 ? WINDOW_EDGE : 0)
      && last <= drawnRows.to - (drawnRows.to < total ? WINDOW_EDGE : 0);
    if (covered || allRowsDrawn || rowDragging()) return;
  }
  const from = allRowsDrawn ? 0 : Math.max(0, first - WINDOW_MARGIN);
  const to = allRowsDrawn ? total : Math.min(total, last + WINDOW_MARGIN);
  const spacer = (count) => (count > 0 ? `<div class="gantt-rows-spacer" style="height:${count * rowHeight}px"></div>` : "");
  const drawn = ganttRows.slice(from, to);

  labelsEl.innerHTML = total
    ? spacer(from) + drawn.map(labelRowHtml).join("") + spacer(total - to)
    : `<div class="gantt-empty">${t("views.gantt.empty")}</div>`;
  const layer = bodyEl.querySelector(".gantt-timeline-scroll > .gantt-calendar-layer");
  if (layer) {
    while (layer.nextSibling) layer.nextSibling.remove();
    layer.insertAdjacentHTML("afterend", spacer(from)
      + drawn.map((row) => renderRow(row, rowContext.dayW, rowContext.isTagMatch(row.node))).join("")
      + spacer(total - to));
  }
  drawnRows = { from, to, rowHeight };
  attachLabelHandlers();
  setupBarInteractions(drawn);
}

/** Every row's node id, in order, drawn or not (Shift+click ranges in the
 * task list, interfaces/classic/ui/multi-select.js: only the rows near the screen are drawn). */
export function rowIds() {
  return ganttRows.map((row) => row.node.id);
}

/** Run `fn` with every row drawn (the SVG export reads them from the page),
 * then draw the rows on screen again. */
export function withAllRowsDrawn(fn) {
  if (!bodyEl || !rowContext) return fn();
  allRowsDrawn = true;
  drawRows(bodyEl.scrollTop, true);
  try {
    return fn();
  } finally {
    allRowsDrawn = false;
    drawRows(bodyEl.scrollTop, true);
  }
}

/** Paint one spare viewport at either side; task bars remain mounted on scroll. */
function calendarMarkup(scrollLeft) {
  const { dayW, timelineWidth } = timelineCache;
  const viewport = bodyEl.clientWidth;
  calendarPaintBounds = { left: scrollLeft - viewport, right: scrollLeft + viewport * 2 };
  const calendar = buildGanttCalendar(timelineBounds.start, timelineBounds.end + 1, dayW, {
    from: timelineBounds.start + calendarPaintBounds.left / dayW,
    to: timelineBounds.start + calendarPaintBounds.right / dayW,
  });
  const today = calendarDay(new Date());
  const todayX = (today - timelineBounds.start) * dayW;
  const todayVisible = todayX >= 0 && todayX < timelineWidth;
  const todayDate = dateStringAtDay(today);
  const header = calendar.levels.map(level => `<div class="gantt-timeline-row ${level.unit}s">${level.periods.map(period => {
    const todayClass = level.unit === 'day' && period.start === today ? ' today' : '';
    const weekendClass = level.unit === 'day' && period.weekend ? ' weekend' : '';
    return `<div class="gantt-timeline-cell${todayClass}${weekendClass}" data-start-date="${dateStringAtDay(period.start)}" style="left:${period.left}px;width:${period.width}px">${period.width >= level.textWidth ? `<span class="sticky-label">${period.label}</span>` : ''}</div>`;
  }).join('')}</div>`).join('') + (todayVisible
    ? `<div class="gantt-today-line" style="left:${todayX}px" role="img" aria-label="${t("views.gantt.todayAt", { date: todayDate })}"><span class="gantt-today-label">${t("common.today")}</span></div>` : '');
  const lines = calendar.lines.map(line => `<div class="gantt-grid-line ${line.unit}-boundary" data-date="${line.date}" style="left:${line.x}px"></div>`).join('');
  const weekends = (calendar.levels.find(level => level.unit === 'day')?.periods || [])
    .filter(period => period.weekend)
    .map(period => `<div class="gantt-weekend-overlay" style="left:${period.left}px;width:${period.width}px"></div>`).join('');
  const grid = lines + weekends + (todayVisible
    ? `<div class="gantt-today-line" style="left:${todayX}px"></div>` : '');
  return { header, grid, height: calendar.levels.length * HEADER_ROW_PX };
}

function refreshCalendarOnScroll() {
  if (!bodyEl || !calendarPaintBounds) return;
  const left = bodyEl.scrollLeft, width = bodyEl.clientWidth;
  if (left >= calendarPaintBounds.left + width / 4 && left + width <= calendarPaintBounds.right - width / 4) return;
  const calendar = calendarMarkup(left);
  daysEl.innerHTML = calendar.header;
  const layer = bodyEl.querySelector('.gantt-calendar-layer');
  if (layer) layer.innerHTML = calendar.grid;
}

function scheduleMidnightRefresh() {
  const next = new Date();
  next.setHours(24, 0, 0, 50);
  midnightTimer = setTimeout(() => {
    renderGantt();
    scheduleMidnightRefresh();
  }, next.getTime() - Date.now());
}

// Execution statistics for the current render (planned vs actual layer)
let execStats = null;

function localDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Actual span of a node from the execution stats: first entered → last worked
 * day, extended to today while the node is still on the current Today list.
 * @returns {{start: Date, end: Date, worked: Date[]}|null}
 */
function executionSpanOf(nodeId) {
  const n = execStats?.nodes?.[nodeId];
  if (!n || !n.firstEntered) return null;
  const start = localDay(new Date(n.firstEntered));
  if (Number.isNaN(start.getTime())) return null;
  const worked = (n.workedDates || []).map((d) => new Date(`${d}T00:00:00`)).filter((d) => !Number.isNaN(d.getTime()));
  let end = worked.length ? worked[worked.length - 1] : start;
  if (execStats.inExecution?.has(nodeId)) {
    const today = localDay(new Date());
    if (today > end) end = today;
  }
  if (end < start) end = start;
  return { start, end, worked };
}

/**
 * Collect rows for the Gantt chart
 */
function collectRows(focused, vis) {
  const rows = [];
  const { doneHidden, isDone, isTagHidden } = vis;

  // visibilityFilter for leaves: same logic as the visibleIds set in
  // doRenderGantt, applied per-node.
  const leafVisible = (n) =>
    !(doneHidden && isDone(n)) && !isTagHidden(n);

  function collect(node, level = 0) {
    if (!node) return;
    // Done-cascade prunes the whole subtree — bubble/mindmap parity.
    if (doneHidden && isDone(node)) return;

    const selfHidden = isTagHidden(node);
    const hasChildren = node.children && node.children.length > 0;
    const taskRange = getGanttTaskRange(node);
    const hasDates = taskRange && !taskRange.placeholder;
    const exec = selfHidden ? null : executionSpanOf(node.id);
    const routineSpan=selfHidden?null:routineBounds(node.id);
    const returnDate = selfHidden ? null : callbacks.getTaskPostponement?.(node.id)?.until;
    const spans = [exec, routineSpan, returnDate ? { start: dateAtDay(calendarDay(returnDate)), end: dateAtDay(calendarDay(returnDate)) } : null].filter(Boolean);
    const range = spans.length ? { start: new Date(Math.min(...spans.map(s => +s.start))), end: new Date(Math.max(...spans.map(s => +s.end))) } : null;
    const planned = taskRange ? { start: dateAtDay(taskRange.startDay), end: dateAtDay(taskRange.endDay) } : null;

    if (hasChildren) {
      let minStart = null,
        maxEnd = null;

      if (hasDates && !selfHidden) {
        minStart = planned.start;
        maxEnd = planned.end;
      }
      if (range) {
        if (minStart === null || range.start < minStart) minStart = range.start;
        if (maxEnd === null || range.end > maxEnd) maxEnd = range.end;
      }

      // Get all leaves for date range — skip Done-cascaded and tag-hidden.
      const leaves = getLeaves(node).filter(
        (n) => n.startDate && n.endDate && leafVisible(n),
      );

      if (leaves.length > 0) {
        const descMinStart = new Date(
          Math.min(...leaves.map((n) => dateAtDay(calendarDay(n.startDate)))),
        );
        const descMaxEnd = new Date(
          Math.max(...leaves.map((n) => dateAtDay(calendarDay(n.endDate)))),
        );

        if (minStart === null || descMinStart < minStart)
          minStart = descMinStart;
        if (maxEnd === null || descMaxEnd > maxEnd) maxEnd = descMaxEnd;
      }

      // Only use proposed dates for the group when it has no real date span.
      // Unscheduled children must not extend a group's committed schedule.
      const placeholder = !minStart || !maxEnd;
      if (placeholder) {
        const proposed = getLeaves(node).filter(leafVisible).map(n => getGanttTaskRange(n)).filter(Boolean);
        if (proposed.length) {
          minStart = dateAtDay(Math.min(...proposed.map(r => r.startDay)));
          maxEnd = dateAtDay(Math.max(...proposed.map(r => r.endDay)));
        } else if (planned) {
          minStart = planned.start;
          maxEnd = planned.end;
        }
      }
      if (minStart && maxEnd && !selfHidden) {
        rows.push({ node, level, isGroup: true, minStart, maxEnd, planned, exec, placeholder });
      }

      // A folded group keeps its row (and its date span) but hides its
      // children; the focused node at the top always shows its own.
      if (!node.collapsed || level === 0) node.children?.forEach((child) => collect(child, level + 1));
    } else if ((planned || range) && !selfHidden) {
      const minStart = planned && (!range || planned.start <= range.start) ? planned.start : range.start;
      const maxEnd = planned && (!range || planned.end >= range.end) ? planned.end : range.end;
      rows.push({ node, level, isGroup: false, minStart, maxEnd, planned, exec, placeholder: !!taskRange?.placeholder });
    }
  }

  collect(focused);
  return rows;
}

/**
 * Get all leaf nodes
 */
function getLeaves(node) {
  if (!node.children || node.children.length === 0) return [node];
  return node.children.flatMap(getLeaves);
}

/**
 * Render a single row
 */
function renderRow(row, cellWidth, tagMatch = false) {
  let barHtml = "";

  // Pixel geometry of an inclusive [start, end] day span on the current scale.
  const geom = (start, end) => {
    const startOffset = calendarDay(start) - calendarDay(timelineCache.minD);
    const endOffset = calendarDay(end) - calendarDay(timelineCache.minD);
    const left = startOffset * cellWidth;
    const width = (endOffset - startOffset + 1) * cellWidth;
    const barPadding = Math.min(6, cellWidth * 0.15);
    return { left, width: Math.max(2, width - barPadding) };
  };

  const isFocusedNode = row.node.id === currentState.focusId;
  const editClass = isFocusedNode ? "not-editable" : "editable";
  const name = row.node.name || row.node.text || t("common.untitled");
  const rowHeight = getRowHeightPx();
  const color = safeColor(row.node.color);
  const status = callbacks?.getEffectiveStatus?.(row.node) || row.node.status || 'todo';

  // Planned layer: the node's own dates (leaf) or the aggregated span (group).
  // Drawn as today's solid bar when there is no actual layer; as a dashed
  // outline when an actual span exists, so the two never blend into one.
  const plannedSpan = row.isGroup
    ? (row.minStart && row.maxEnd ? { start: row.minStart, end: row.maxEnd } : null)
    : row.planned;
  if (plannedSpan) {
    const { left, width } = geom(plannedSpan.start, plannedSpan.end);
    const barHeight = Math.round(rowHeight * 0.9);
    const barRadius = Math.round(barHeight / 2);
    const outline = !!row.exec && !row.isGroup;
    const style = row.placeholder
      ? `left:${left}px;width:${width}px;height:${barHeight}px;border-radius:${barRadius}px;background:transparent`
      : outline
      ? `left:${left}px;width:${width}px;height:${barHeight}px;border-radius:${barRadius}px;background:transparent;border:2px dashed ${color};box-sizing:border-box;color:${color};text-shadow:none`
      : `left:${left}px;width:${width}px;height:${barHeight}px;border-radius:${barRadius}px;background:${color}`;
    barHtml += `<div class="gantt-bar ${row.isGroup ? "group-bar" : ""} ${editClass}${row.placeholder ? " unscheduled" : outline ? " planned-outline" : ""}"
      style="${style}"
      data-id="${escapeAttr(row.node.id)}" data-is-group="${row.isGroup}" data-status="${status}"
      data-start-date="${dateStringAtDay(calendarDay(plannedSpan.start))}" data-end-date="${dateStringAtDay(calendarDay(plannedSpan.end))}"
      title="${row.placeholder ? t("views.gantt.unscheduled", { name: escapeAttr(name) }) : outline ? t("views.gantt.planned", { name: escapeAttr(name) }) : escapeAttr(name)}">
      ${!isFocusedNode ? '<div class="gantt-resize-handle gantt-resize-left"></div>' : ""}
      <span class="gantt-bar-label">${escapeHtmlCore(name)}</span>
      ${!isFocusedNode ? '<div class="gantt-resize-handle gantt-resize-right"></div>' : ""}
    </div>`;
  }

  // Actual layer: first entered → last worked (or today while listed), with a
  // mark on every day ticked as worked on. Non-interactive.
  if (row.exec && !row.isGroup) {
    const { left, width } = geom(row.exec.start, row.exec.end);
    const h = Math.max(1, Math.round(rowHeight * 0.25));
    const top = (rowHeight - h) / 2;
    const ticks = row.exec.worked.map((d) => {
      const g = geom(d, d);
      return `<div class="gantt-tick" style="left:${g.left}px;width:${Math.max(2, Math.min(g.width, 6))}px;height:${h}px;top:${top}px" title="${t("views.gantt.workedOn", { date: dateStringAtDay(calendarDay(d)) })}"></div>`;
    }).join("");
    const workedDays = row.exec.worked.length;
    barHtml += `<div class="gantt-actual" style="left:${left}px;width:${width}px;height:${h}px;top:${top}px;background:${color}"
      title="${t("views.gantt.actual", { count: workedDays })}">
      ${!plannedSpan ? `<span class="gantt-actual-label">${escapeHtmlCore(name)}</span>` : ""}
    </div>${ticks}`;
  }

  const returnDate = callbacks.getTaskPostponement?.(row.node.id)?.until;
  if (returnDate && timelineBounds) {
    const x = (calendarDay(returnDate) - timelineBounds.start + 0.5) * timelineCache.dayW;
    barHtml += `<button type="button" class="gantt-task-return" data-task-postpone="${escapeAttr(row.node.id)}" style="left:${x}px;top:${getRowHeightPx()/2}px" title="${t("views.gantt.returnToList", { date: returnDate })}" aria-label="${t("views.gantt.returnToList", { date: returnDate })}">◆</button>`;
  }
  if(timelineBounds)for(const mark of routineDates(row.node.id,dateStringAtDay(timelineBounds.start),dateStringAtDay(timelineBounds.end))) {
    const g=geom(dateAtDay(calendarDay(mark.day)),dateAtDay(calendarDay(mark.day)));
    const h=Math.max(3,Math.round(rowHeight*(mark.kind==='actual'?.25:.65)));
    barHtml+=`<button type="button" class="gantt-routine-mark ${mark.kind}" data-routine-open="${escapeAttr(row.node.id)}" style="left:${g.left}px;width:${Math.max(3,g.width)}px;height:${h}px;top:${(rowHeight-h)/2}px;--routine-color:${color}" title="${t(mark.kind==='actual'?'views.gantt.sessionRecorded':'views.gantt.scheduledRoutine',{date:mark.day})}" aria-label="${t('views.gantt.openRoutine',{date:mark.day})}"></button>`;
  }
  const rowClass = tagMatch ? "gantt-row tag-match" : "gantt-row";
  return `<div class="${rowClass}" data-id="${escapeAttr(row.node.id)}" data-status="${callbacks?.getEffectiveStatus?.(row.node) || row.node.status || 'todo'}" style="height:${getRowHeightPx()}px">${barHtml}</div>`;
}

/**
 * Setup scroll synchronization
 */
function setupScrollSync() {
  let scrollLock = false;

  bodyEl.onscroll = () => {
    drawRows();
    if (scrollLock) return;
    scrollLock = true;
    headerEl.scrollLeft = bodyEl.scrollLeft;
    labelsEl.scrollTop = bodyEl.scrollTop;
    refreshCalendarOnScroll();
    if (timelineBounds && !bodyEl.querySelector('.dragging')) {
      const threshold = bodyEl.clientWidth * 0.75;
      const nearStart = bodyEl.scrollLeft < threshold;
      const nearEnd = bodyEl.scrollWidth - bodyEl.scrollLeft - bodyEl.clientWidth < threshold;
      if (nearStart || nearEnd) {
        const extraDays = Math.max(7, Math.ceil(bodyEl.clientWidth / timelineCache.dayW) * 2);
        if (nearStart) timelineBounds.start -= extraDays;
        if (nearEnd) timelineBounds.end += extraDays;
        renderGantt();
      }
    }
    requestAnimationFrame(() => {
      scrollLock = false;
    });
  };

  labelsEl.onscroll = () => {
    if (scrollLock) return;
    scrollLock = true;
    bodyEl.scrollTop = labelsEl.scrollTop;
    requestAnimationFrame(() => {
      scrollLock = false;
    });
  };
}

/**
 * Setup depth-aware label drag-drop for tree restructuring.
 * Cursor X position controls target depth (like sidebar tree and markdown view).
 */
function setupLabelDragDrop() {
  // Abort previous listeners to prevent stacking on re-render
  if (dragDropAbort) dragDropAbort.abort();
  dragDropAbort = new AbortController();
  const signal = dragDropAbort.signal;

  const DEPTH_INDENT = 16; // px per indent level in gantt labels
  const COLOR_PALETTE = [
    "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16", "#22c55e",
    "#10b981", "#14b8a6", "#06b6d4", "#0ea5e9", "#3b82f6", "#6366f1",
    "#8b5cf6", "#a855f7", "#d946ef", "#ec4899", "#f43f5e",
  ];

  let draggedNodeId = null;
  let dropIndicator = null;
  let dropGuides = null;
  let lastGuideKey = '';
  let dropTarget = null;
  let highlightedParent = null;

  const isViewer = callbacks.isReadOnly && callbacks.isReadOnly();

  function isDescendantOf(ancestorId, nodeId) {
    let current = callbacks.findParent(nodeId);
    while (current) {
      if (current.id === ancestorId) return true;
      current = callbacks.findParent(current.id);
    }
    return false;
  }

  function getDescendantIds(nodeId) {
    const ids = [];
    const node = callbacks.find(nodeId);
    if (!node) return ids;
    (function walk(n) {
      if (n.children) {
        for (const child of n.children) { ids.push(child.id); walk(child); }
      }
    })(node);
    return ids;
  }

  function getNewColor(existingColors) {
    for (const color of COLOR_PALETTE) {
      if (!existingColors.includes(color)) return color;
    }
    return COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)];
  }

  function applyColorToSubtree(nodeId, color) {
    if (callbacks.onUpdateNode) callbacks.onUpdateNode(nodeId, { color });
    const node = callbacks.find(nodeId);
    if (node?.children) {
      for (const child of node.children) applyColorToSubtree(child.id, color);
    }
  }

  function getVisibleRows() {
    return Array.from(labelsEl.querySelectorAll('.gantt-label-row:not(.dragging)'));
  }

  // Row-rect snapshot for the drag (same pattern as the sidebar tree drag):
  // rows don't move during a drag, so capture once in scroll-invariant
  // content coordinates instead of getBoundingClientRect per row per
  // dragover event (thousands of forced-layout reads interleaved with the
  // indicator style writes).
  let dragRowRects = null;
  let dragLabelsRect = null;

  function captureDragRects() {
    dragLabelsRect = labelsEl.getBoundingClientRect();
    const scrollTop = labelsEl.scrollTop;
    dragRowRects = getVisibleRows().map((el) => {
      const r = el.getBoundingClientRect();
      return {
        el,
        contentTop: r.top - dragLabelsRect.top + scrollTop,
        contentMid: r.top + r.height / 2 - dragLabelsRect.top + scrollTop,
        contentBottom: r.bottom - dragLabelsRect.top + scrollTop,
      };
    });
  }

  // Rows drag only by their grip. dragstart's target is the draggable row
  // itself in Chromium (never the grip inside it), so remember where the
  // press started instead of testing the dragstart target.
  let pressedOnHandle = false;
  labelsEl.addEventListener('mousedown', (e) => {
    pressedOnHandle = !!e.target.closest('.gantt-drag-handle');
  }, { signal });

  // ---- dragstart ----
  labelsEl.addEventListener('dragstart', (e) => {
    if (!pressedOnHandle) { e.preventDefault(); return; }
    pressedOnHandle = false;
    if (isViewer) { e.preventDefault(); return; }
    const row = e.target.closest('.gantt-label-row');
    if (!row) return;

    draggedNodeId = row.dataset.id;

    // Mark dragged row + descendant rows
    row.classList.add('dragging');
    for (const id of getDescendantIds(draggedNodeId)) {
      const el = labelsEl.querySelector(`.gantt-label-row[data-id="${id}"]`);
      if (el) el.classList.add('dragging');
    }

    // Custom ghost
    const ghost = document.createElement('div');
    ghost.style.cssText = 'position:absolute;top:-9999px;padding:6px 12px;background:var(--bg-secondary);border:1px solid var(--border-color);border-radius:6px;font-size:12px;color:var(--text-primary);white-space:nowrap;';
    ghost.textContent = MultiSelect.movesSelection(draggedNodeId)
      ? t('views.dragNodes', { count: MultiSelect.getIds().length })
      : callbacks.find(draggedNodeId)?.name || t('views.node');
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => ghost.remove(), 0);
    e.dataTransfer.effectAllowed = 'move';
  }, { signal });

  // ---- dragover ----
  labelsEl.addEventListener('dragover', (e) => {
    if (!draggedNodeId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // (Re)capture row rects: first dragover, or after a remote store update
    // re-rendered the rows mid-drag (detached cached elements).
    if (!dragRowRects || (dragRowRects.length > 0 && !dragRowRects[0].el.isConnected)) {
      captureDragRects();
    }
    if (dragRowRects.length === 0) return;

    // Find above/below rows at cursor Y, in scroll-invariant content coords
    const contentY = e.clientY - dragLabelsRect.top + labelsEl.scrollTop;
    let aboveEntry = null;
    let belowEntry = null;
    for (const entry of dragRowRects) {
      if (contentY >= entry.contentMid) {
        aboveEntry = entry;
      } else {
        belowEntry = entry;
        break;
      }
    }
    const aboveRow = aboveEntry?.el || null;
    const belowRow = belowEntry?.el || null;

    // Depth range
    const aboveLevel = aboveRow ? parseInt(aboveRow.dataset.level) || 0 : -1;
    const belowLevel = belowRow ? parseInt(belowRow.dataset.level) || 0 : 0;
    const maxDepth = aboveRow ? aboveLevel + 1 : 0;
    const minDepth = (belowRow && belowLevel > aboveLevel) ? aboveLevel + 1 : 0;

    // Map cursor X to target depth (offset by base padding of 20px)
    const relativeX = e.clientX - dragLabelsRect.left - 20;
    let targetDepth = Math.floor(relativeX / DEPTH_INDENT);
    targetDepth = Math.max(minDepth, Math.min(targetDepth, maxDepth));

    // Determine parent and insert index
    const rootId = currentState.data?.id;
    let parentId = null;
    let insertIndex = 0;

    if (aboveRow) {
      const aboveNodeId = aboveRow.dataset.id;
      if (targetDepth === aboveLevel + 1) {
        // Child of row above
        parentId = aboveNodeId;
        if (belowRow) {
          const belowNodeId = belowRow.dataset.id;
          const belowParent = callbacks.findParent(belowNodeId);
          if (belowParent?.id === aboveNodeId) {
            const siblings = belowParent.children || [];
            const idx = siblings.findIndex(c => c.id === belowNodeId);
            insertIndex = idx >= 0 ? idx : siblings.length;
          } else {
            insertIndex = (callbacks.find(aboveNodeId)?.children || []).length;
          }
        } else {
          insertIndex = (callbacks.find(aboveNodeId)?.children || []).length;
        }
      } else {
        // Walk up from aboveRow to find ancestor at targetDepth
        let currentId = aboveNodeId;
        for (let d = aboveLevel; d >= targetDepth; d--) {
          const parent = callbacks.findParent(currentId);
          if (!parent) break;
          insertIndex = (parent.children || []).findIndex(c => c.id === currentId) + 1;
          currentId = parent.id;
        }
        parentId = currentId;
      }
    } else {
      parentId = rootId;
      insertIndex = 0;
    }

    dropTarget = { parentId, insertIndex };

    // ---- Show drop indicator ----
    if (!dropIndicator) {
      dropIndicator = document.createElement('div');
      dropIndicator.className = 'gantt-drop-indicator';
      labelsEl.appendChild(dropIndicator);
    }

    let indicatorTop;
    if (aboveEntry) {
      indicatorTop = aboveEntry.contentBottom + 1;
    } else if (belowEntry) {
      indicatorTop = belowEntry.contentTop - 1;
    } else {
      indicatorTop = 0;
    }

    const indent = 20 + targetDepth * DEPTH_INDENT;
    dropIndicator.style.left = `${indent}px`;
    dropIndicator.style.top = `${indicatorTop}px`;

    // ---- Show depth guides ----
    if (!dropGuides) {
      dropGuides = document.createElement('div');
      dropGuides.className = 'gantt-drop-guides';
      labelsEl.appendChild(dropGuides);
    }
    dropGuides.style.top = `${indicatorTop}px`;

    const guideKey = `${minDepth}-${maxDepth}`;
    if (guideKey !== lastGuideKey) {
      lastGuideKey = guideKey;
      dropGuides.innerHTML = '';

      if (maxDepth > minDepth) {
        const line = document.createElement('div');
        line.className = 'gantt-drop-guide-line';
        line.style.left = `${20 + minDepth * DEPTH_INDENT}px`;
        line.style.width = `${(maxDepth - minDepth) * DEPTH_INDENT}px`;
        dropGuides.appendChild(line);
      }

      for (let d = minDepth; d <= maxDepth; d++) {
        const dot = document.createElement('div');
        dot.className = 'gantt-drop-guide-dot';
        dot.style.left = `${20 + d * DEPTH_INDENT}px`;
        dropGuides.appendChild(dot);
      }
    }

    // ---- Highlight target parent row ----
    if (highlightedParent) {
      highlightedParent.classList.remove('drop-parent');
      highlightedParent = null;
    }
    if (parentId && parentId !== rootId) {
      const parentRow = labelsEl.querySelector(`.gantt-label-row[data-id="${parentId}"]`);
      if (parentRow) {
        parentRow.classList.add('drop-parent');
        highlightedParent = parentRow;
      }
    }
  }, { signal });

  // ---- dragleave ----
  labelsEl.addEventListener('dragleave', (e) => {
    if (!labelsEl.contains(e.relatedTarget)) {
      removeDropVisuals();
    }
  }, { signal });

  // ---- drop ----
  labelsEl.addEventListener('drop', (e) => {
    e.preventDefault();
    if (!draggedNodeId || !dropTarget) { cleanup(); return; }

    const { parentId, insertIndex } = dropTarget;
    if (!parentId || parentId === draggedNodeId) { cleanup(); return; }
    if (isDescendantOf(draggedNodeId, parentId)) { cleanup(); return; }

    // A row of the multi-selection moves them all, then each moved branch
    // takes its new colour like a single move.
    if (MultiSelect.movesSelection(draggedNodeId)) {
      const moving = MultiSelect.topLevelIds((id) => callbacks.findParent(id));
      const { movedIds } = MultiSelect.moveSelectionTo(parentId, insertIndex);
      if (movedIds.length) {
        const rootNode = currentState.data;
        const used = (rootNode.children || []).filter(c => !moving.includes(c.id)).map(c => c.color);
        callbacks.beginBatch?.();
        try {
          for (const id of moving.filter((m) => movedIds.includes(m))) {
            const color = parentId === rootNode.id ? getNewColor(used) : callbacks.find(parentId)?.color;
            if (!color) continue;
            used.push(color);
            applyColorToSubtree(id, color);
          }
        } finally {
          callbacks.endBatch?.();
        }
      }
      cleanup();
      return;
    }

    // Adjust index if moving within same parent
    let idx = insertIndex;
    const dragParent = callbacks.findParent(draggedNodeId);
    if (dragParent?.id === parentId) {
      const dragIndex = (dragParent.children || []).findIndex(c => c.id === draggedNodeId);
      if (dragIndex >= 0 && dragIndex < idx) idx--;
    }

    // Move the node
    if (callbacks.onMoveNode) {
      callbacks.onMoveNode(draggedNodeId, parentId, idx);
    }

    // Handle color: root children get unique color, others inherit parent's
    const rootId = currentState.data?.id;
    if (parentId === rootId) {
      const rootNode = currentState.data;
      const existingColors = (rootNode.children || [])
        .filter(c => c.id !== draggedNodeId)
        .map(c => c.color);
      const newColor = getNewColor(existingColors);
      applyColorToSubtree(draggedNodeId, newColor);
    } else {
      const parentNode = callbacks.find(parentId);
      if (parentNode?.color) {
        applyColorToSubtree(draggedNodeId, parentNode.color);
      }
    }

    cleanup();
  }, { signal });

  // ---- dragend ----
  labelsEl.addEventListener('dragend', () => { cleanup(); }, { signal });

  function removeDropVisuals() {
    if (dropIndicator) { dropIndicator.remove(); dropIndicator = null; }
    if (dropGuides) { dropGuides.remove(); dropGuides = null; lastGuideKey = ''; }
    if (highlightedParent) { highlightedParent.classList.remove('drop-parent'); highlightedParent = null; }
  }

  function cleanup() {
    removeDropVisuals();
    labelsEl.querySelectorAll('.dragging').forEach(el => el.classList.remove('dragging'));
    draggedNodeId = null;
    dropTarget = null;
    dragRowRects = null;
    dragLabelsRect = null;
  }
}

/**
 * Setup bar interactions (drag/resize)
 */
function setupBarInteractions(rows) {
  const { dayW: cellWidth, timelineWidth } = timelineCache;
  const isViewer = callbacks.isReadOnly && callbacks.isReadOnly();

  bodyEl.querySelectorAll(".gantt-bar").forEach((bar) => {
    const nodeId = bar.dataset.id;
    const isGroup = bar.classList.contains("group-bar");

    if (nodeId === currentState.focusId) return;

    // Double-click to edit
    bar.addEventListener("dblclick", () => {
      if (isViewer) return;
      if (callbacks.onNodeEdit) {
        callbacks.onNodeEdit(nodeId);
      }
    });

    // Single click to select
    bar.addEventListener("click", (e) => {
      e.stopPropagation();
      if (callbacks.onNodeSelect) {
        callbacks.onNodeSelect(nodeId);
      }
    });

    // Drag/resize
    let dragging = false;
    let resizing = false;
    let resizeLeft = false;
    let startX, origLeft, origWidth;
    let descendantBars = []; // Track descendant bars for group drag

    bar.addEventListener("mousedown", (e) => {
      if (e.button !== 0) return;
      // Disable drag/resize for read-only viewers
      if (isViewer) return;
      e.preventDefault();
      e.stopPropagation();

      dragging = true;
      startX = e.clientX;
      origLeft = parseFloat(bar.style.left);
      origWidth = parseFloat(bar.style.width);

      const handle = e.target.closest(".gantt-resize-handle");
      resizing = !!handle;
      resizeLeft = handle?.classList.contains("gantt-resize-left");

      // For group bars (not resizing), collect all descendant bars
      descendantBars = [];
      if (isGroup && !resizing) {
        const node = callbacks.find(nodeId);
        if (node) {
          // Collect all descendant node IDs
          const descendantIds = [];
          const collectDescendants = (n) => {
            if (n.children) {
              n.children.forEach((child) => {
                descendantIds.push(child.id);
                collectDescendants(child);
              });
            }
          };
          collectDescendants(node);

          // Find bars for these descendants and store their original positions
          descendantIds.forEach((id) => {
            const descBar = bodyEl.querySelector(`.gantt-bar[data-id="${id}"]`);
            if (descBar) {
              descendantBars.push({
                el: descBar,
                origLeft: parseFloat(descBar.style.left),
              });
            }
          });
        }
      }

      bar.classList.add("dragging");
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    });

    function onMove(e) {
      if (!dragging) return;
      const dx = e.clientX - startX;

      if (resizing) {
        if (resizeLeft) {
          let newLeft = origLeft + dx;
          let newWidth = origWidth - dx;
          if (newWidth < cellWidth) {
            newWidth = cellWidth;
            newLeft = origLeft + origWidth - cellWidth;
          }
          if (newLeft < 0) {
            newWidth = origWidth + origLeft;
            newLeft = 0;
          }
          bar.style.left = newLeft + "px";
          bar.style.width = newWidth + "px";
        } else {
          let newWidth = Math.max(cellWidth, origWidth + dx);
          bar.style.width = newWidth + "px";
        }
      } else {
        let newLeft = origLeft + dx;
        newLeft = Math.max(
          0,
          Math.min(timelineWidth - parseFloat(bar.style.width), newLeft),
        );
        bar.style.left = newLeft + "px";

        // Move all descendant bars by the same delta
        const actualDx = newLeft - origLeft;
        descendantBars.forEach(({ el, origLeft: descOrigLeft }) => {
          let descNewLeft = descOrigLeft + actualDx;
          descNewLeft = Math.max(
            0,
            Math.min(timelineWidth - parseFloat(el.style.width), descNewLeft),
          );
          el.style.left = descNewLeft + "px";
        });
      }
    }

    function onUp() {
      if (!dragging) return;
      dragging = false;
      bar.classList.remove("dragging");
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);

      const node = callbacks.find(nodeId);
      if (!node) return;

      const newLeft = parseFloat(bar.style.left);
      const newWidth = parseFloat(bar.style.width);
      let shiftDays = Math.round((newLeft - origLeft) / cellWidth);
      let widthChangeDays = Math.round(
        (newWidth - origWidth) / cellWidth,
      );

      const MAX_SHIFT = 36500;
      shiftDays = Math.max(-MAX_SHIFT, Math.min(MAX_SHIFT, shiftDays));
      widthChangeDays = Math.max(
        -MAX_SHIFT,
        Math.min(MAX_SHIFT, widthChangeDays),
      );

      if (!isFinite(shiftDays) || !isFinite(widthChangeDays)) return;
      if (shiftDays === 0 && widthChangeDays === 0) return;

      if (resizing) {
        handleResize(node, resizeLeft, shiftDays, widthChangeDays, bar.dataset);
      } else {
        handleDrag(node, isGroup, shiftDays, bar.dataset);
      }
    }
  });
}

/**
 * Safe date update helper
 */
function safeUpdateDate(dateStr, daysDelta) {
  const day = calendarDay(dateStr);
  if (!Number.isFinite(day) || !Number.isFinite(daysDelta)) return null;
  try { return dateStringAtDay(day + daysDelta); } catch { return null; }
}

/** Resizing confirms both endpoints, including a previously unscheduled task. */
function handleResize(node, isLeftResize, shiftDays, widthChangeDays, displayed) {
  const startDate = safeUpdateDate(displayed.startDate, isLeftResize ? shiftDays : 0);
  const endDate = safeUpdateDate(displayed.endDate, isLeftResize ? 0 : widthChangeDays);
  if (!startDate || !endDate || endDate < startDate) return;
  // Let the normal write guards run before the Store receives any date changes.
  setTimeout(() => callbacks?.onUpdateNode?.(node.id, {startDate, endDate}), 0);
}

/** Moving a group shifts its dated descendants and confirms proposed leaf bars. */
function handleDrag(node, isGroup, shiftDays, displayed) {
  const updates = [];
  const addShift = (n, range) => {
    if (!range) return;
    const startDate = safeUpdateDate(range.startDate, shiftDays);
    const endDate = safeUpdateDate(range.endDate, shiftDays);
    if (startDate && endDate) updates.push({id: n.id, startDate, endDate});
  };
  if (isGroup) {
    const walk = n => {
      const range = getGanttTaskRange(n);
      if (range && (!range.placeholder || !n.children?.length)) {
        addShift(n, {startDate: dateStringAtDay(range.startDay), endDate: dateStringAtDay(range.endDay)});
      }
      n.children?.forEach(walk);
    };
    walk(node);
  } else {
    addShift(node, displayed);
  }
  if (updates.length) {
    setTimeout(() => {
      if (!callbacks?.onUpdateNode) return;
      warnMissingBatchCallbacks();
      callbacks.beginBatch?.();
      try {
        for (const {id, startDate, endDate} of updates) callbacks.onUpdateNode(id, {startDate, endDate});
      } finally { callbacks.endBatch?.(); }
    }, 0);
  }
}

/**
 * Setup zoom handler - centered on mouse position
 */
function setupZoomHandler() {
  if (!bodyEl) return;

  // Alt+wheel zoom centered on mouse position
  bodyEl.addEventListener(
    "wheel",
    (e) => {
      if (!e.altKey) return;

      e.preventDefault();

      // Get mouse position relative to timeline content
      const bodyRect = bodyEl.getBoundingClientRect();
      const mouseXInView = e.clientX - bodyRect.left;
      const scrollLeft = bodyEl.scrollLeft;
      const mouseXInTimeline = mouseXInView + scrollLeft;

      // A calendar anchor survives range extension as well as zoom changes.
      scrollAnchor = { day: calendarDay(timelineCache.minD) + mouseXInTimeline / timelineCache.dayW, x: mouseXInView };

      // Apply zoom
      const delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
      const zoomFactor = delta < 0 ? 1.15 : 1 / 1.15;
      ganttZoom = Math.max(0.01, Math.min(10, ganttZoom * zoomFactor));

      if (callbacks.onZoomChange) {
        callbacks.onZoomChange(ganttZoom);
      }

      renderGantt();

    },
    { passive: false },
  );

  // Keyboard shortcuts for zoom: + and -
  const keyHandler = (e) => {
    // Only handle if Gantt view is active
    if (!container || !container.closest(".view.active")) return;

    // Don't handle if typing in input
    if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;

    if (e.key === "+" || e.key === "=") {
      e.preventDefault();
      ganttZoom = Math.min(10, ganttZoom * 1.15);
      if (callbacks.onZoomChange) {
        callbacks.onZoomChange(ganttZoom);
      }
      renderGantt();
    } else if (e.key === "-" || e.key === "_") {
      e.preventDefault();
      ganttZoom = Math.max(0.01, ganttZoom / 1.15);
      if (callbacks.onZoomChange) {
        callbacks.onZoomChange(ganttZoom);
      }
      renderGantt();
    }
  };

  document.addEventListener("keydown", keyHandler);

  // Store handler for cleanup
  container._ganttKeyHandler = keyHandler;
}

/**
 * Render breadcrumbs
 */
function renderBreadcrumbs() {
  const crumbsEl = container.querySelector("#g-crumbs");
  if (!crumbsEl) return;
  renderHorizonControl(container, currentState.horizon, callbacks.onHorizonChange, currentState.currentDocId);

  const path = [];
  let current = callbacks.find(currentState.focusId);

  while (current) {
    path.unshift(current);
    current = callbacks.findParent(current.id);
  }

  const escapeHtml = callbacks.escapeHtml || ((s) => s);

  // View indicator
  const viewIndicator = `<span class="view-indicator">${viewLabel('gantt')}</span>`;

  if (!path || path.length === 0) {
    const rootData = currentState.data;
    crumbsEl.innerHTML =
      viewIndicator +
      `<div class="crumb current" data-id="${escapeAttr(rootData.id)}">
        <span class="crumb-dot" style="background:${safeColor(rootData.color, "#6366f1")}"></span>${escapeHtml(rootData.name || t("views.root"))}
      </div>`;
    return;
  }

  crumbsEl.innerHTML =
    viewIndicator +
    path
      .map((node, i) => {
        const isLast = i === path.length - 1;
        const name = node.name || node.text || t("views.root");
        const sep = i > 0 ? '<span class="crumb-sep">›</span>' : "";
        return `${sep}<div class="crumb ${isLast ? "current" : ""}" data-id="${escapeAttr(node.id)}">
        <span class="crumb-dot" style="background:${safeColor(node.color, "#6366f1")}"></span>${escapeHtml(name)}
      </div>`;
      })
      .join("");

  crumbsEl.querySelectorAll(".crumb:not(.current)").forEach((crumb) => {
    crumb.addEventListener("click", () => {
      if (callbacks.onNavigate) {
        callbacks.onNavigate(crumb.dataset.id);
      }
    });
  });
}

/** Center the task's planned bar (or execution bar for an undated task). */
export function centerOnNode(nodeId) {
  let row = bodyEl?.querySelector(`.gantt-row[data-id="${CSS.escape(nodeId)}"]`);
  if (!row && bodyEl) {
    // Not drawn: bring its place on screen and draw there.
    const index = ganttRows.findIndex((r) => r.node.id === nodeId);
    if (index < 0) return false;
    const rowHeight = getRowHeightPx();
    bodyEl.scrollTop = index * rowHeight - (bodyEl.clientHeight - rowHeight) / 2;
    labelsEl.scrollTop = bodyEl.scrollTop;
    drawRows(bodyEl.scrollTop, true);
    row = bodyEl.querySelector(`.gantt-row[data-id="${CSS.escape(nodeId)}"]`);
  }
  const bar = row?.querySelector('.gantt-bar, .gantt-actual');
  const offset = centeringOffset(bodyEl, bar || row);
  if (!offset) return false;
  if (bar) bodyEl.scrollLeft -= offset.x;
  bodyEl.scrollTop -= offset.y;
  headerEl.scrollLeft = bodyEl.scrollLeft;
  labelsEl.scrollTop = bodyEl.scrollTop;
  return true;
}

/** Update the view with new state. */
export function update(prevState, nextState) {
  currentState = nextState;

  // Only update zoom if it actually changed in the state (not due to our local changes)
  // This prevents zoom reset when dragging timebars
  if (nextState.ganttZoom && prevState.ganttZoom !== nextState.ganttZoom) {
    ganttZoom = nextState.ganttZoom;
  }

  // Only update row height if it actually changed in the state
  if (nextState.ganttRowHeight && prevState.ganttRowHeight !== nextState.ganttRowHeight) {
    ganttRowHeightPct = nextState.ganttRowHeight;
  }

  if (container && callbacks) renderGantt();
}

/**
 * Clean up the view
 */
export function destroy() {
  if (renderFrame !== null) cancelAnimationFrame(renderFrame);
  renderFrame = null;
  viewportObserver?.disconnect();
  viewportObserver = null;
  clearTimeout(midnightTimer);
  midnightTimer = null;
  calendarPaintBounds = null;
  timelineBounds = null;
  scrollAnchor = null;
  ganttRows = [];
  rowContext = null;
  drawnRows = null;
  allRowsDrawn = false;
  // Remove keyboard handler
  if (container && container._ganttKeyHandler) {
    document.removeEventListener("keydown", container._ganttKeyHandler);
  }

  if (container) {
    container.innerHTML = "";
  }
  if (dragDropAbort) { dragDropAbort.abort(); dragDropAbort = null; }
  container = null;
  callbacks = null;
  currentState = null;
  daysEl = null;
  scaleEl = null;
  labelsEl = null;
  bodyEl = null;
  headerEl = null;
  ganttZoom = 1;
  ganttRowHeightPct = 100;
  timelineCache = {
    minD: null,
    dayW: 40,
    timelineWidth: 0,
  };
  renderScheduled = false;
  warnedMissingCallbacks = false; // Reset so warning can fire again on next render if still broken
}

// Export view info
export const viewInfo = {
  name: "gantt",
  get label() { return viewLabel("gantt"); },
  icon: "chart-gantt",
};
